import { FileBarChart } from 'lucide-react';
import type { ComponentType } from 'react';

import ApiIcon from '@/components/icons/Api';
import DBIcon from '@/components/icons/Database';
import DocumentIcon from '@/components/icons/Document';
import ModelContextProtocolIcon from '@/components/icons/ModelContextProtocol';
import PowerBIIcon from '@/components/icons/PowerBI';

export const SOURCE_CATEGORY_TABS = [
    { id: 'all', label: 'All' },
    { id: 'reports', label: 'Reports' },
    { id: 'databases', label: 'Databases' },
    { id: 'files', label: 'Files' },
    { id: 'apis', label: 'APIs' },
    { id: 'documents', label: 'Documents' }
] as const;

export type SourceCategoryTabId = (typeof SOURCE_CATEGORY_TABS)[number]['id'];

type SourceIconComponent = ComponentType<{ className?: string }>;

type SourceTypeDefinition = {
    label: string;
    category: Exclude<SourceCategoryTabId, 'all'>;
    icon: SourceIconComponent;
};

const SOURCE_TYPE_DEFINITIONS: Record<string, SourceTypeDefinition> = {
    semantic_model: {
        label: 'Report',
        category: 'reports',
        icon: PowerBIIcon
    },
    sql: {
        label: 'Database',
        category: 'databases',
        icon: DBIcon
    },
    excel: {
        label: 'File',
        category: 'files',
        icon: FileBarChart
    },
    csv: {
        label: 'File',
        category: 'files',
        icon: FileBarChart
    },
    file: {
        label: 'File',
        category: 'files',
        icon: FileBarChart
    },
    api: {
        label: 'API',
        category: 'apis',
        icon: ApiIcon
    },
    document: {
        label: 'Document',
        category: 'documents',
        icon: DocumentIcon
    }
};

export function normalizeSourceConfigType(configType?: string | null) {
    return configType?.toLowerCase().trim() || '';
}

export function getSourceTypeDefinition(configType?: string | null) {
    const normalizedType = normalizeSourceConfigType(configType);
    return SOURCE_TYPE_DEFINITIONS[normalizedType] ?? null;
}

export function isKnownSourceType(configType?: string | null) {
    return Boolean(getSourceTypeDefinition(configType));
}

export function getSourceTypeIcon(
    configType?: string | null
): SourceIconComponent {
    // Every source badge here is backed by an MCP tool call under the hood
    // (see mcp_loader.py) — an unrecognized/unset config_type isn't "not a
    // file", it's just a source without a more specific known type, so the
    // MCP logo reads better as the generic fallback than an arbitrary file
    // icon that implies a document that may not exist.
    return getSourceTypeDefinition(configType)?.icon ?? ModelContextProtocolIcon;
}

export function getSourceCategory(
    configType?: string | null
): SourceCategoryTabId {
    return getSourceTypeDefinition(configType)?.category ?? 'all';
}
